import { useEffect, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

import type { Site } from '@klareospace/pharos-lib-types';

// ─── Hook ─────────────────────────────────────────────────────────────────────

// Reset form when the site data actually changes on the server (after successful update + refetch,
// or navigating to a different site). We depend on `site.id` and `site.updatedAt` instead of the
// full `site` object to avoid losing dirty state due to parent re-renders causing new object
// references. `site.id` alone is not enough: switching between sites whose `updatedAt` happen to be
// identical (e.g. seeded fixtures) would otherwise leave the previous site's value in the form.
export function useResetFormOnSiteUpdate<T extends FieldValues>(
  form: UseFormReturn<T>,
  site: Site,
  toDefaults: (site: Site) => T,
): void {
  const { reset } = form;
  const siteRef = useRef(site);
  siteRef.current = site;

  useEffect(() => {
    reset(toDefaults(siteRef.current));
  }, [site.id, site.updatedAt, reset, toDefaults]);
}
